import React, { useState, useEffect, useLayoutEffect, lazy, Suspense, useMemo } from 'react';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import { Outlet, useLocation } from 'react-router-dom';
import WelcomeGuideModal from './Onboarding/WelcomeGuideModal';
import TermsAgreementModal from './SuperAdmin/TermsAgreementModal';
import { getCurrentUser } from '../utils/auth';
import { usePlan } from '../contexts/PlanContext';
import LazyBoundary from './Common/LazyBoundary';
import WhatsNewModal from './WhatsNew/WhatsNewModal';
import ImpersonationBanner from './Impersonate/ImpersonationBanner';
import AdminPageGuard from './Common/AdminPageGuard';
import { isImpersonating } from '../utils/impersonation';
import apiClient from '../utils/apiClient';
import { opensPhoneShell } from '../utils/phoneShell';
import { hidesAssistant } from '../utils/studentShell';

const StudentPhoneApp = lazy(() => import('./StudentMobile/shell/StudentPhoneApp'));
const StudentDeskApp = lazy(() => import('./StudentDesk/shell/StudentDeskApp'));
const AIDoubtChatWidget = lazy(() => import('./AIDoubtChat/AIDoubtChatWidget'));
const AdminAssistantWidget = lazy(() => import('./AdminAssistant/AdminAssistantWidget'));

const AppLayout = () => {
  const location = useLocation();
  const { hasPlanFeature } = usePlan();
  const isChatPage = location.pathname.endsWith('/chat');

  // Campus Copilot shows only on the admin dashboard, desktop width only.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 992px)').matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const showCopilot =
    isDesktop &&
    location.pathname === '/dashboard' &&
    ['admin', 'superadmin'].includes(getCurrentUser()?.usertype);

  const hideAIWidget = useMemo(() => hidesAssistant(location.pathname), [location.pathname]);
  const [sidebarShow, setSidebarShow] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [whatsNewRelease, setWhatsNewRelease] = useState(null);

  // Platform support session: the banner is fixed above the top nav and the
  // desktop sidebar, both of which sit at top: 0, so the shell has to be pushed
  // down by its height. The class carries that offset (impersonation.scss).
  //
  // ⚠️ A LAYOUT effect, not a passive one. The class now also DECLARES
  // --shell-banner-h (tokens/_space.scss), which the banner's own `height`
  // reads and which every bounded page column subtracts — so applying it after
  // paint would show a zero-height banner, and a column 34px too tall, for one
  // frame on every load of a support session.
  const impersonating = isImpersonating();
  useLayoutEffect(() => {
    document.body.classList.toggle('has-imp-banner', impersonating);
    return () => document.body.classList.remove('has-imp-banner');
  }, [impersonating]);

  useEffect(() => {
    const user = getCurrentUser();
    // Show terms modal for institute Super Admin who hasn't accepted
    if (user?.usertype === 'superadmin' && !user?.termsAccepted) {
      setShowTerms(true);
    }
    // Show onboarding wizard for superadmin users who haven't completed it
    if (user?.usertype === 'superadmin' || user?.usertype === 'admin') {
      // Check if user dismissed onboarding this session
      if (!sessionStorage.getItem('onboarding-dismissed')) {
        setShowOnboarding(true);
      }

      // Check for What's New releases (skip if already skipped this session)
      if (!sessionStorage.getItem('whats-new-skipped')) {
        apiClient.get('/whats-new/check')
          .then(({ data }) => { if (data.release) setWhatsNewRelease(data.release); })
          .catch(() => {});
      }
    }
  }, []);

  const handleWhatsNewDone = async () => {
    if (whatsNewRelease?._id) {
      try { await apiClient.post(`/whats-new/${whatsNewRelease._id}/dismiss`); } catch (_) {}
    }
    setWhatsNewRelease(null);
  };

  const handleWhatsNewSkip = () => {
    sessionStorage.setItem('whats-new-skipped', whatsNewRelease?._id || '1');
    setWhatsNewRelease(null);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    sessionStorage.setItem('onboarding-dismissed', '1');
  };

  return (
    <div className="app-layout">
      {/* Platform support session banner — renders only while impersonating */}
      <ImpersonationBanner />

      {/* Fixed Header */}
      <TopNav onMenuClick={() => setSidebarShow(true)} />
      
      <div className="app-container">
        {/* Desktop Sidebar */}
        <div className="d-none d-md-block">
          <Sidebar show={true} onHide={() => {}} isOffcanvas={false} />
        </div>
        
        {/* Mobile Offcanvas Sidebar */}
        <Sidebar show={sidebarShow} onHide={() => setSidebarShow(false)} isOffcanvas={true} />
        
        {/* Main Content */}
        <div className={`main-content-wrapper${isChatPage ? ' no-padding' : ''}`}>
          {/* An add / edit page the admin's permissions do not allow says so (data/pageAccess). */}
          <AdminPageGuard><Outlet /></AdminPageGuard>
        </div>
      </div>

      {/* Welcome guide — modal overlay of first-run shortcuts */}
      <WelcomeGuideModal
        show={showOnboarding}
        onComplete={handleOnboardingComplete}
      />

      {/* Terms & Conditions — blocking modal for tenantadmin first-time */}
      {showTerms && (
        <TermsAgreementModal onAccepted={() => setShowTerms(false)} />
      )}

      {/* What's New — carousel modal for institute admins */}
      {whatsNewRelease && (
        <WhatsNewModal
          release={whatsNewRelease}
          onDone={handleWhatsNewDone}
          onSkip={handleWhatsNewSkip}
        />
      )}

      {/* AI Doubt Chat — floating widget for students (plan-gated, hidden during practice/exams) */}
      {getCurrentUser()?.usertype === 'student' && !hideAIWidget && hasPlanFeature('aiDoubtSolving') && (
        <LazyBoundary>
          <Suspense fallback={null}>
            <AIDoubtChatWidget />
          </Suspense>
        </LazyBoundary>
      )}

      {/* Campus Copilot — floating assistant, dashboard + desktop only */}
      {showCopilot && (
        <LazyBoundary>
          <Suspense fallback={null}>
            <AdminAssistantWidget />
          </Suspense>
        </LazyBoundary>
      )}
    </div>
  );
};

/**
 * A student gets an app shell: the phone's below 768px, the floating menu above it.
 * Everyone else gets the layout above untouched. College menus are not in either
 * shell yet, and the plan says which one this is, so a shell waits for it.
 */
const Layout = () => {
  const { institution, loading } = usePlan();
  const isStudent = getCurrentUser()?.usertype === 'student';
  if (!isStudent || institution?.navLayout === 'academic') return <AppLayout />;
  if (loading) return <div className="tw-min-h-dvh tw-bg-surface" />;
  return (
    <Suspense fallback={<div className="tw-min-h-dvh tw-bg-surface" />}>
      {opensPhoneShell() ? <StudentPhoneApp /> : <StudentDeskApp />}
    </Suspense>
  );
};

export default Layout;
